import React from 'react';
import { Box, Typography } from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { Alert, AlertInline, AsyncButton, Button, DatePicker, Modal, ModalActions, ModalContent, SubmitButton, TextInput } from '@uplift/ui-v2';
import { CalendarDateService, DateObject } from '@uplift/services';
import { useUpdateProviderOutOfOfficeMutation, useDeleteProviderOutOfOfficeMutation } from '@uplift/queries';
import { UpdateOutOfOfficeEventBody } from '@uplift/api';


export type OOOInitialValues = {
  id?: string;
  title: string;
  startDate: DateObject;
  endDate: DateObject;
};

export type UpdateOutOfOfficeModalProps = {
  open: boolean;
  onClose: () => void;
  providerId: string;
  initialValues: OOOInitialValues;
  existingEvents?: OOOInitialValues[];
};

type OOOFormValues = {
  title: string;
  startDate: DateObject | null;
  endDate: DateObject | null;
};



export const UpdateOutOfOfficeModal: React.FC<UpdateOutOfOfficeModalProps> = ({ open, onClose, providerId, initialValues, existingEvents = [] }) => {
  const [ submitError, setSubmitError ] = React.useState<string | undefined>(undefined);
  const updateOutOfOfficeMutation = useUpdateProviderOutOfOfficeMutation();
  const deleteOutOfOfficeMutation = useDeleteProviderOutOfOfficeMutation();

  const validationSchema = React.useMemo(() => {
    const otherEvents = existingEvents.filter((e) => e.id !== initialValues?.id);

    const dateIsInsideEvent = (date: DateObject) =>
      otherEvents.some((e) =>
        !date.isBefore(e.startDate, 'day') && !date.isAfter(e.endDate, 'day')
      );


    return Yup.object({
      title: Yup.string().required('Event title is required').max(40, 'Title must be 40 characters or less'),
      startDate: Yup.mixed<DateObject>()
        .required('Start date is required'),
        endDate: Yup.mixed<DateObject>()
        .required('End date is required')
        .test('after-start', 'End date must be on or after the start date', function (value) {
          const { startDate } = this.parent as OOOFormValues;

          if (!startDate || !value) return true;
          return !startDate.isAfter(value as DateObject, 'day');
        })
        .test('no-overlap', 'This overlaps with an existing out of office event', function (value) {
          if (!value) return true;
          return !dateIsInsideEvent(value as DateObject);
        }),
    });
  }, [ existingEvents, initialValues?.id ]);

  return (
    <Formik<OOOFormValues>
      enableReinitialize
      validateOnMount
      initialValues={initialValues ?? { title: '', startDate: null, endDate: null }}
      validationSchema={validationSchema}
      onSubmit={async (values, { resetForm }) => {
        setSubmitError(undefined);
        try {
          if (values.startDate && values.endDate) {
            const payload: UpdateOutOfOfficeEventBody = {};

            if (values.title !== initialValues.title) payload.eventTitle = values.title;
            if (!values.startDate.isSame(initialValues.startDate)) payload.startDate = values.startDate.format('YYYY-MM-DD');
            if (!values.endDate.isSame(initialValues.endDate)) payload.endDate = values.endDate.format('YYYY-MM-DD');

            await updateOutOfOfficeMutation.mutateAsync({
              providerId,
              outOfOfficeId: initialValues.id ?? '',
              ...payload,
            });
          }
          resetForm();
          onClose();
        } catch (err) {
          console.error('[OOO submit error]', (err as { response?: { data?: unknown } })?.response?.data ?? err);
          setSubmitError('Something went wrong. Please try again.');
        }
      }}
    >
      {({ values, resetForm }) => {
        const handleClose = () => {
          resetForm();
          setSubmitError(undefined);
          onClose();
        };

        return (
          <Modal open={open} onClose={handleClose} maxWidth="md" title="Edit Out of Office" showCloseIcon>
            <ModalContent>
              <Typography variant="body1" mb={2}>
                Patients will see a notification when they try to message you during this time period.
                This is something you need to coordinate coverage for.
              </Typography>
              <TextInput
                name="title"
                label="Event Title"
                placeholder="e.g., Vacation, Conference, Personal Leave"
                fullWidth
                helperText={`${values.title.length}/40`}
              />

              {/* Date row */}
              <Box display="flex" gap={2} mt={2}>
                <Box flex={1} >
                  <DatePicker
                    name="startDate"
                    label="Start Date"
                    fullWidth
                    disablePast
                    format="MM/DD/YYYY"
                    minDate={CalendarDateService.date()}
                  />
                </Box>
                <Box flex={1} >
                  <DatePicker
                    name="endDate"
                    label="End Date"
                    fullWidth
                    disablePast
                    format="MM/DD/YYYY"
                    minDate={values.startDate ?? undefined}
                  />
                </Box>
              </Box>

              <Alert severity="info" sx={{ mt: 3 }} showIcon={false}>
                <strong>Note:</strong> This does not remove or block your availability. To prevent
                bookings, you&apos;ll need to adjust your calendar separately.
              </Alert>
            </ModalContent>

            {submitError && (
              <Box sx={{ px: 3, pb: 1 }}>
                <AlertInline severity="error">{submitError}</AlertInline>
              </Box>
            )}
            <ModalActions sx={{ gap: 1 }}>
              <AsyncButton variant="text" color="error" onClick={async () => {
                await deleteOutOfOfficeMutation.mutateAsync({ providerId, outOfOfficeId: initialValues?.id ?? '' });
                handleClose();
              }} sx={{ mr: 'auto' }}>
                Delete
              </AsyncButton>
              <Button variant="outlined" onClick={handleClose}>Cancel</Button>
                  <SubmitButton
                    variant="contained"
                    color="secondary"
                    disabledOnInvalid
                  >
                    Save
                  </SubmitButton>
            </ModalActions>
          </Modal>
        );
      }}
    </Formik>
  );
};
